import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Checkbox } from '@atlaskit/checkbox';
import { FormValues, SUCCESS_CHAMPION_PREFERENCES } from '../../../types/ChampionApplicationFormTypes';

const PRIVACY_POLICY_URL = 'https://www.atlassian.com/legal/privacy-policy#what-this-policy-covers';
/** Path only; must use `/assets/` so Global Edge / bifrost serve them like other static files (root `/champion-*.pdf` hits SPA → Invalid Path). */
const CHAMPION_NDA_PDF_PATH = '/assets/champion-nda.pdf';
const CHAMPION_PA_PDF_PATH = '/assets/champion-pa.pdf';

type FieldErrors = {
  consentProgramAgreement?: string;
  consentPrivacyPolicy?: string;
  consentNda?: string;
  consentSuccessChampionAgreement?: string;
};

type Props = {
  /** Same as `ChampionsApplyPage` → `ChampionApplicationForm`: local dev vs `HOME_BASE_URL` per env. */
  homeBaseUrl: string;
  values: FormValues;
  updateField: <K extends keyof FormValues>(key: K, value: FormValues[K]) => void;
  errors?: FieldErrors;
};

export const AgreementsSection: React.FC<Props> = ({ homeBaseUrl, values, updateField, errors }) => {
  const ndaPdfUrl = `${homeBaseUrl}${CHAMPION_NDA_PDF_PATH}`;
  const paPdfUrl = `${homeBaseUrl}${CHAMPION_PA_PDF_PATH}`;
  const showSuccessChampionDataAgreement =
    values.championPreference !== null &&
    SUCCESS_CHAMPION_PREFERENCES.includes(values.championPreference);

  const privacyLabel = (
    <>
      I acknowledge the{' '}
      <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" css={inlineLinkStyles}>
        Atlassian Privacy Policy
      </a>
    </>
  );

  const ndaLabel = (
    <>
      By checking this box, I agree to the{' '}
      <a href={ndaPdfUrl} target="_blank" rel="noopener noreferrer" css={inlineLinkStyles}>
        Non-Disclosure Agreement
      </a>
      .
    </>
  );

  const programAgreementLabel = (
    <>
      By checking this box, I acknowledge that I have read, understood, and agree to abide by the{' '}
      <a href={paPdfUrl} target="_blank" rel="noopener noreferrer" css={inlineLinkStyles}>
        Atlassian Champions Program Agreement
      </a>
      {" and to uphold Atlassian's values in all my activities as a Champion."}
    </>
  );

  return (
    <div css={sectionBoxStyles}>
      <div css={controlTopStyles}>
        <div
          css={agreementRowStyles}
          className={
            errors?.consentProgramAgreement
              ? 'champion-form-agreements champion-form-agreements-program--error'
              : 'champion-form-agreements'
          }
        >
          <div css={checkboxRowStyles} className="champion-form-agreements-checkbox-row">
            <abbr css={requiredStyles} title="required" aria-hidden="true">
              *
            </abbr>
            <Checkbox
              name="consent_program_agreement"
              label={programAgreementLabel}
              isChecked={values.consentProgramAgreement}
              onChange={() =>
                updateField('consentProgramAgreement', !values.consentProgramAgreement)
              }
            />
          </div>
        </div>

        <div
          css={agreementRowTopStyles}
          className={
            errors?.consentPrivacyPolicy
              ? 'champion-form-agreements champion-form-agreements-privacy--error'
              : 'champion-form-agreements'
          }
        >
          <div css={checkboxRowStyles} className="champion-form-agreements-checkbox-row">
            <abbr css={requiredStyles} title="required" aria-hidden="true">
              *
            </abbr>
            <Checkbox
              name="consent_privacy_policy"
              label={privacyLabel}
              isChecked={values.consentPrivacyPolicy}
              onChange={() => updateField('consentPrivacyPolicy', !values.consentPrivacyPolicy)}
            />
          </div>
        </div>

        <div
          css={agreementRowTopStyles}
          className={
            errors?.consentNda
              ? 'champion-form-agreements champion-form-agreements-nda--error'
              : 'champion-form-agreements'
          }
        >
          <div css={checkboxRowStyles} className="champion-form-agreements-checkbox-row">
            <abbr css={requiredStyles} title="required" aria-hidden="true">
              *
            </abbr>
            <Checkbox
              name="consent_nda"
              label={ndaLabel}
              isChecked={values.consentNda}
              onChange={() => updateField('consentNda', !values.consentNda)}
            />
          </div>
        </div>

        {showSuccessChampionDataAgreement && (
          <div
            css={agreementRowTopStyles}
            className={
              errors?.consentSuccessChampionAgreement
                ? 'champion-form-agreements champion-form-agreements-success-champion-data--error'
                : 'champion-form-agreements'
            }
          >
            <div css={checkboxRowStyles} className="champion-form-agreements-checkbox-row">
              <abbr css={requiredStyles} title="required" aria-hidden="true">
                *
              </abbr>
              <Checkbox
                name="consent_success_champion_agreement"
                label={
                  <>
                    Atlassian may use the information provided by me, together with my profile and activity on the
                    Atlassian platform, to confirm my affiliation with an active Atlassian account and support me as a
                    Success Champion.
                  </>
                }
                isChecked={values.consentSuccessChampionAgreement}
                onChange={() =>
                  updateField(
                    'consentSuccessChampionAgreement',
                    !values.consentSuccessChampionAgreement,
                  )
                }
              />
            </div>
          </div>
        )}
      </div>

      {(errors?.consentProgramAgreement ||
        errors?.consentPrivacyPolicy ||
        errors?.consentNda ||
        errors?.consentSuccessChampionAgreement) && (
        <div css={errorStyles}>
          {errors.consentProgramAgreement ||
            errors.consentPrivacyPolicy ||
            errors.consentNda ||
            errors.consentSuccessChampionAgreement}
        </div>
      )}
    </div>
  );
};

const sectionBoxStyles = css({
  marginBottom: '40px',
});

const controlTopStyles = css({
  marginTop: token('space.050'),
});

const checkboxRowStyles = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: '0.25rem',
});

const agreementRowStyles = css({
  display: 'flex',
  flexDirection: 'column',
});

const agreementRowTopStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginTop: token('space.050'),
});

const requiredStyles = css({
  color: 'var(--champion-form-error)',
  textDecoration: 'none',
  marginRight: '0.125rem',
});

const inlineLinkStyles = css({
  color: 'var(--champion-form-blue)',
  textDecoration: 'none',
});

const errorStyles = css({
  color: 'var(--champion-form-error)',
  fontFamily: 'sans-serif',
  fontSize: '0.75rem',
  fontWeight: 400,
  marginTop: token('space.050'),
});
